import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorker;

// Fallback x-boundaries (in PDF points) of the "Quote Detail" table's columns - only used when
// extractColumnBoundaries (below) can't find that table's own header row.
const DEFAULT_COLUMN_MAX_X = {
  description: 340,
  uom: 390,
  unit: 450,
  rate: 495
};

const QUOTE_SUMMARY_COLUMN_MAX_X = {
  language: 150,
  linguistic: 210,
  desktopPublishing: 293,
  engineering: 369,
  projectManagement: 445
};

// Per vendor, per locale: [managementFeePercentage, translateNoMatchRate, translatePostEditNoMatchRate]
// translatePostEditNoMatchRate is null where the vendor's rate card has no post-edit step for that locale.
const RATE_CARDS = {
  centific: {
    'fr-FR': [8, 0.19, null], 'nl-NL': [8, 0.21, null], 'it-IT': [8, 0.175, null],
    'ja-JP': [8, 0.25, null], 'ru-RU': [8, 0.13, null], 'sv-SE': [8, 0.22, null],
    'ko-KR': [8, 0.12, 0.11], 'zh-CN': [8, 0.07, 0.06], 'tr-TR': [8, 0.16, 0.14],
    'de-DE': [8, 0.17, 0.15], 'es-ES': [8, 0.121, 0.135], 'pl-PL': [8, 0.13, null],
    'zh-TW': [8, 0.11, 0.1], 'pt-BR': [8, 0.11, 0.1]
  },
  milengo: {
    'cs-CZ': [10, 0.1, null], 'pl-PL': [10, 0.1, 0.075], 'zh-CN': [10, 0.1, 0.075],
    'nl-NL': [10, 0.165, 0.13], 'fr-FR': [10, 0.13, 0.095], 'sv-SE': [10, 0.18, 0.12],
    'ja-JP': [10, 0.22, 0.153], 'zh-TW': [10, 0.135, 0.114], 'de-DE': [10, 0.13, 0.085],
    'ko-KR': [10, 0.14, 0.115], 'pt-BR': [10, 0.105, 0.075], 'it-IT': [10, 0.105, 0.075],
    'es-ES': [10, 0.105, 0.06], 'ru-RU': [10, 0.1, 0.075], 'tr-TR': [10, 0.105, 0.075]
  }
};

const LANGUAGE_TO_LOCALE = {
  'French (France)': 'fr-FR',
  'Italian (Italy)': 'it-IT',
  Japanese: 'ja-JP',
  'Dutch (Netherlands)': 'nl-NL',
  Polish: 'pl-PL',
  Russian: 'ru-RU',
  'Swedish (Sweden)': 'sv-SE',
  German: 'de-DE',
  'German (Germany)': 'de-DE',
  Spanish: 'es-ES',
  Korean: 'ko-KR',
  'Portuguese (Brazil)': 'pt-BR',
  'Portuguese (Brasil)': 'pt-BR',
  Turkish: 'tr-TR',
  'Chinese (Simplified)': 'zh-CN',
  'Chinese (Traditional)': 'zh-TW',
  'Chinese (Traditional, Taiwan)': 'zh-TW',
  Czech: 'cs-CZ'
};

const VENDOR_DISPLAY_NAME = {
  milengo: 'Milengo GmbH',
  centific: 'Centific'
};

const Y_SAME_LINE_GAP = 2;
const Y_WRAP_GAP = 12;
const Y_MATCH_TOLERANCE = 10;
// DITA project quotes drop the language from the sub-total line entirely ("Translation sub-total", no "English > Lang,"),
// so its language comes from extractRows's headerLanguage tracking instead (see parseQuoteUnits).
const ROW_START_RE = /Documentation Translation \(|Software Localization|Translation sub-total/i;
const DESC_RE = /(?:Documentation Translation \((New Words|Fuzzies)\)|Software Localization),\s*English\s*>\s*((?:[^,()]|\([^)]*\))+),/i;
const JIRA_ID_RE = /DAGE-\d{4}/;
const PROJECT_NAME_LABEL = 'Project Name';
const PROJECT_NAME_VALUE_X = [80, 220];
const LEFT_LABEL_MAX_X = 80;
const QUOTE_SUMMARY_FOOTER_RE = /^(Total|Discount:|Subtotal|VAT:|Grand Total)/;

// Quotes spell Chinese variants inconsistently between Quote Detail and Quote Summary (e.g. "Chinese (simpl.,
// China)" vs "Chinese (simpl. China)" for Simplified, "Chinese (Traditional, Taiwan)" vs "Chinese (Traditional)"
// for Traditional), so normalize any variant mentioning Chinese plus the relevant qualifier to the one key Quote
// Summary uses.
function normalizeLanguage(language) {
  if (!/chinese/i.test(language)) return language;
  if (/simpl/i.test(language)) return 'Chinese (Simplified)';
  if (/traditional/i.test(language)) return 'Chinese (Traditional)';
  return language;
}

// Different quotes lay the Quote Detail table out at different x-positions (observed: the UoM column as far left
// as x=358, as far right as x=406), so fixed thresholds eventually misclassify a column for some quote. Deriving
// the boundaries from that quote's own "Description | UoM | Unit | Rate | Cost" header row - the midpoint between
// each pair of header labels - adapts automatically instead.
function extractColumnBoundaries(page1Items) {
  const headerX = (label) => page1Items.find((item) => item.str === label)?.x;
  const description = headerX('Description');
  const uom = headerX('UoM');
  const unit = headerX('Unit');
  const rate = headerX('Rate');
  const cost = headerX('Cost');
  if ([description, uom, unit, rate, cost].some((x) => x === undefined)) return DEFAULT_COLUMN_MAX_X;

  const midpoint = (a, b) => (a + b) / 2;
  return {
    // Unlike the other (numeric) columns, Description holds free text that can run almost all the way up to UoM's
    // own data (e.g. a hyphen-wrapped "sub-" tail landing right before "Words") rather than stopping at the
    // midpoint between the two header labels, so anchor this boundary on UoM's x instead, with a small margin so
    // "Words" itself still lands on the UoM side of it.
    description: uom - 15,
    uom: midpoint(uom, unit),
    unit: midpoint(unit, rate),
    rate: midpoint(rate, cost)
  };
}

function classifyColumn(x, columnMaxX) {
  if (x < columnMaxX.description) return 'description';
  if (x < columnMaxX.uom) return 'uom';
  if (x < columnMaxX.unit) return 'unit';
  if (x < columnMaxX.rate) return 'rate';
  return 'cost';
}

function classifyQuoteSummaryColumn(x) {
  if (x < QUOTE_SUMMARY_COLUMN_MAX_X.language) return 'language';
  if (x < QUOTE_SUMMARY_COLUMN_MAX_X.linguistic) return 'linguistic';
  if (x < QUOTE_SUMMARY_COLUMN_MAX_X.desktopPublishing) return 'desktopPublishing';
  if (x < QUOTE_SUMMARY_COLUMN_MAX_X.engineering) return 'engineering';
  if (x < QUOTE_SUMMARY_COLUMN_MAX_X.projectManagement) return 'projectManagement';
  return 'totalPerLanguage';
}

async function getPageItems(page) {
  const viewport = page.getViewport({ scale: 1 });
  const textContent = await page.getTextContent();
  return textContent.items
    .map((item) => {
      const [, , , , x, y] = item.transform;
      return { str: item.str, x, y: viewport.height - y };
    })
    .filter((item) => item.str.trim().length > 0);
}

function groupIntoLines(items) {
  const lines = [];
  items.forEach((item) => {
    const line = lines.find((candidate) => Math.abs(candidate.y - item.y) <= Y_SAME_LINE_GAP);
    if (line) {
      line.parts.push(item);
      line.y = Math.min(line.y, item.y);
    } else {
      lines.push({ y: item.y, parts: [item] });
    }
  });
  return lines
    .map((line) => ({
      y: line.y,
      text: line.parts.sort((a, b) => a.x - b.x).map((part) => part.str).join(' ')
    }))
    .sort((a, b) => a.y - b.y);
}

function nearestByY(items, targetY) {
  let nearest = null;
  let nearestDistance = Infinity;
  items.forEach((item) => {
    const distance = Math.abs(item.y - targetY);
    if (distance < nearestDistance) {
      nearest = item;
      nearestDistance = distance;
    }
  });
  return nearestDistance <= Y_MATCH_TOLERANCE ? nearest : null;
}

// Same as nearestByY, but tries several candidate Y's - a row spanning 3+ lines doesn't have one obvious "center"
// to anchor the numeric columns to, so just take whichever of its lines lands closest.
function nearestByAnyY(items, targetYs) {
  let best = null;
  let bestDistance = Infinity;
  targetYs.forEach((targetY) => {
    const candidate = nearestByY(items, targetY);
    if (candidate) {
      const distance = Math.abs(candidate.y - targetY);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }
  });
  return best;
}

// Quote Detail lists languages one after another, each opening with a bare-language-name row (same as Quote Summary).
// `knownLanguages` (from Quote Summary) and `initialLanguage` (carried over a page break) let us track the current
// language as a fallback for rows (e.g. DITA's "Translation sub-total") that don't repeat it themselves.
function extractRows(items, columnMaxX, knownLanguages, initialLanguage) {
  const byColumn = { description: [], uom: [], unit: [], cost: [] };
  items.forEach((item) => {
    const column = classifyColumn(item.x, columnMaxX);
    if (column in byColumn) byColumn[column].push(item);
  });

  // pdfjs occasionally fuses the UoM label and the adjacent Unit value into one text run (e.g. "Words 139.0" on
  // some sub-total rows) instead of two separate items - split those back apart so the Unit column lookup below
  // still finds a value.
  byColumn.uom = byColumn.uom.flatMap((item) => {
    const merged = item.str.match(/^(Words)\s+([\d,]+\.\d+)$/i);
    if (!merged) return [item];
    byColumn.unit.push({ str: merged[2], x: item.x, y: item.y });
    return [{ str: merged[1], x: item.x, y: item.y }];
  });

  Object.values(byColumn).forEach((column) => column.sort((a, b) => a.y - b.y));

  const lines = groupIntoLines(byColumn.description);
  const rows = [];
  let currentLanguage = initialLanguage || null;
  for (let index = 0; index < lines.length; index += 1) {
    const primary = lines[index];

    // Exact match only - normalizeLanguage's loose "mentions chinese and simpl" check is meant for an
    // already-isolated language string, and would otherwise also fire on a full row-start line like "Software
    // Localization, English > Chinese (simpl., China)," (which contains both words too), silently swallowing it
    // as if it were a bare language-header line instead of extracting it as a data row.
    const trimmed = primary.text.trim();
    if (knownLanguages.has(trimmed)) {
      currentLanguage = trimmed;
      continue;
    }
    if (!ROW_START_RE.test(primary.text)) continue;

    // A long filename can wrap across more than one continuation line (description + filename line + a standalone
    // "sub-total" line), so keep absorbing consecutive lines - stopping at the next row/language marker or once
    // the gap grows too large - rather than just one.
    const rowLines = [primary];
    let next = lines[index + 1];
    let lookahead = index + 1;
    while (next &&
      !ROW_START_RE.test(next.text) &&
      !knownLanguages.has(next.text.trim()) &&
      next.y > rowLines[rowLines.length - 1].y &&
      next.y - rowLines[rowLines.length - 1].y <= Y_WRAP_GAP) {
      rowLines.push(next);
      lookahead += 1;
      next = lines[lookahead];
    }

    // "sub-total" sometimes hyphen-wraps as "sub-" / "total" across lines; don't insert a space in that case or
    // the "sub-total" match below fails.
    const text = rowLines.reduce(
      (acc, line) => (acc === '' ? line.text : `${acc}${acc.endsWith('-') ? '' : ' '}${line.text}`),
      ''
    );
    const uomAnchor = nearestByAnyY(byColumn.uom, rowLines.map((line) => line.y));
    if (!uomAnchor) continue;
    const unit = nearestByY(byColumn.unit, uomAnchor.y);
    const cost = nearestByY(byColumn.cost, uomAnchor.y);
    if (unit && cost) rows.push({ text, units: unit.str, cost: cost.str, headerLanguage: currentLanguage });
  }
  return { rows, currentLanguage };
}

// A fixed Y tolerance around the label misses multi-line values and can sweep in neighboring fields' values when the
// value is a single line, so bound it strictly between this label and its neighbors in the left-hand label column.
function extractHeaderMeta(items) {
  const leftLabels = items.filter((item) => item.x < LEFT_LABEL_MAX_X).sort((a, b) => a.y - b.y);
  const labelIndex = leftLabels.findIndex((item) => item.str === PROJECT_NAME_LABEL);
  const lowerBound = labelIndex > 0 ? leftLabels[labelIndex - 1].y : -Infinity;
  const upperBound = labelIndex >= 0 && labelIndex < leftLabels.length - 1 ? leftLabels[labelIndex + 1].y : Infinity;
  const projectName = labelIndex >= 0 ? items
    .filter((item) => item.x > PROJECT_NAME_VALUE_X[0] && item.x < PROJECT_NAME_VALUE_X[1] &&
      item.y > lowerBound && item.y < upperBound)
    .sort((a, b) => a.y - b.y)
    .map((item) => item.str)
    .join('') : '';
  const pageText = items.map((item) => item.str).join(' ');
  const vendorKey = Object.keys(RATE_CARDS).find((key) => new RegExp(key, 'i').test(pageText)) || null;
  const jiraMatch = projectName.match(JIRA_ID_RE);
  return { projectName, vendorKey, jiraID: jiraMatch ? jiraMatch[0] : '' };
}

function parseCurrency(value) {
  if (!value) return 0;
  const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function extractQuoteSummary(items) {
  const header = items.find((item) => item.str === 'Language' && item.x < QUOTE_SUMMARY_COLUMN_MAX_X.language);
  if (!header) return {};
  const byColumn = {
    language: [],
    linguistic: [],
    desktopPublishing: [],
    engineering: [],
    projectManagement: [],
    totalPerLanguage: []
  };
  items.forEach((item) => {
    if (item.y <= header.y) return;
    const column = classifyQuoteSummaryColumn(item.x);
    if (column in byColumn) byColumn[column].push(item);
  });
  Object.values(byColumn).forEach((column) => column.sort((a, b) => a.y - b.y));

  const summary = {};
  const languageLines = groupIntoLines(byColumn.language);
  for (let index = 0; index < languageLines.length; index += 1) {
    const primary = languageLines[index];
    if (!primary.text) continue;
    if (QUOTE_SUMMARY_FOOTER_RE.test(primary.text)) break;

    const next = languageLines[index + 1];
    const nextHasOwnNumbers = next && (
      nearestByY(byColumn.linguistic, next.y) ||
      nearestByY(byColumn.desktopPublishing, next.y) ||
      nearestByY(byColumn.engineering, next.y) ||
      nearestByY(byColumn.projectManagement, next.y) ||
      nearestByY(byColumn.totalPerLanguage, next.y)
    );
    const hasContinuation = next &&
      !nextHasOwnNumbers &&
      !QUOTE_SUMMARY_FOOTER_RE.test(next.text) &&
      next.y > primary.y && next.y - primary.y <= Y_WRAP_GAP;
    const language = normalizeLanguage(hasContinuation ? `${primary.text} ${next.text}` : primary.text);
    const centerY = hasContinuation ? (primary.y + next.y) / 2 : primary.y;
    if (hasContinuation) index += 1;

    summary[language] = {
      linguisticSubtotal: parseCurrency(nearestByY(byColumn.linguistic, centerY)?.str),
      desktopPublishingSubtotal: parseCurrency(nearestByY(byColumn.desktopPublishing, centerY)?.str),
      engineeringSubtotal: parseCurrency(nearestByY(byColumn.engineering, centerY)?.str),
      projectManagementSubtotal: parseCurrency(nearestByY(byColumn.projectManagement, centerY)?.str),
      totalPerLanguage: parseCurrency(nearestByY(byColumn.totalPerLanguage, centerY)?.str)
    };
  }
  return summary;
}

function round(value, decimals = 2) {
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
}

// Quote Detail's layout varies enough across vendors/project types that it can fail to match anything while Quote
// Summary still parses fine, so languages come from the union of both (a quoteSummary-only language still gets a
// row, just with zeroed newWords/fuzzies figures).
function summarize(sums, quoteSummary) {
  const languages = new Set([...Object.keys(sums), ...Object.keys(quoteSummary)]);
  const emptySums = { newWordsUnits: 0, newWordsCost: 0, fuzziesUnits: 0, fuzziesCost: 0 };
  const summary = {};
  [...languages].sort().forEach((language) => {
    const data = sums[language] || emptySums;
    const pdfSummary = quoteSummary[language] || {};
    summary[language] = {
      newWords: { units: round(data.newWordsUnits, 1), cost: round(data.newWordsCost) },
      fuzzies: { units: round(data.fuzziesUnits, 1), cost: round(data.fuzziesCost) },
      total: {
        units: round(data.newWordsUnits + data.fuzziesUnits, 1),
        cost: round(data.newWordsCost + data.fuzziesCost),
        linguisticSubtotal: pdfSummary.linguisticSubtotal ?? null,
        desktopPublishingSubtotal: pdfSummary.desktopPublishingSubtotal ?? null,
        engineeringSubtotal: pdfSummary.engineeringSubtotal ?? null,
        projectManagementSubtotal: pdfSummary.projectManagementSubtotal ?? null,
        totalPerLanguage: pdfSummary.totalPerLanguage ?? null
      }
    };
  });
  return summary;
}

function determineProduct(projectName) {
  if (/sense_ui/i.test(projectName)) return 'Sense UI';
  if (/plural_ui/i.test(projectName)) return 'Plural UI';
  if (/automl_ui/i.test(projectName)) return 'Automl UI';
  if (/pendo_ui/i.test(projectName)) return 'Pendo UI';
  if (/nprinting_ui/i.test(projectName)) return 'NPrinting UI';
  if (/qlikview_ui/i.test(projectName)) return 'Qlikview UI';
  if (/ui/i.test(projectName)) return 'Talend UI';
  if (/qcs_UA/i.test(projectName)) return 'Sense UA';
  if (/senseDeployAdmin_UA/i.test(projectName)) return 'Sense UA';
  if (/sense_UA/i.test(projectName)) return 'Sense UA';
  if (/qv12_UA/i.test(projectName)) return 'Qlikview UA';
  if (/nprinting_UA/i.test(projectName)) return 'NPrinting UA';
  if (/ua/i.test(projectName)) return 'Talend UA';
  return null;
}

function determineType(projectName) {
  if (/ui/i.test(projectName)) return 'UI';
  if (/ua/i.test(projectName)) return 'UA';
  return null;
}

function buildRecords(summary, meta) {
  const warnings = [];
  const month = new Date().toLocaleString('en-US', { month: 'long' });
  const product = determineProduct(meta.projectName);
  const type = determineType(meta.projectName);
  const records = [];

  Object.keys(summary).forEach((language) => {
    const locale = LANGUAGE_TO_LOCALE[language];
    const rate = locale && RATE_CARDS[meta.vendorKey]?.[locale];
    if (!locale || !rate) {
      warnings.push(`Skipped ${language}: no locale or rate card for ${meta.vendorKey || 'unknown vendor'}.`);
      return;
    }
    const [managementFeePercentage, translateNoMatchRate] = rate;
    const { newWords, total } = summary[language];
    const managementFeeRate = managementFeePercentage / 100;
    const humanBase = round(newWords.units * translateNoMatchRate);
    const includeMtComparison = /qcs_UA/i.test(meta.projectName);
    records.push({
      product,
      orderedIn: month,
      handbackDate: month,
      language: locale.replace(/-/g, '_'),
      vendor: VENDOR_DISPLAY_NAME[meta.vendorKey] || meta.vendorKey,
      type,
      handoffName: meta.projectName,
      currency: 'EUR',
      generatedFromMetrics: total.linguisticSubtotal,
      managementFee: total.projectManagementSubtotal,
      actualInEUR: total.totalPerLanguage,
      jiraID: meta.jiraID,
      comment: '',
      mtWordCount: includeMtComparison ? newWords.units : 0,
      mtCost: includeMtComparison ? round(newWords.cost * (1 + managementFeeRate)) : 0,
      htCost: includeMtComparison ? round(humanBase * (1 + managementFeeRate)) : 0
    });
  });
  return { records, warnings };
}

export async function parseQuoteUnits(arrayBuffer) {
  const document = await getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
  const sums = {};
  let meta = null;
  let quoteSummary = {};
  let currentLanguage = null;
  let columnMaxX = DEFAULT_COLUMN_MAX_X;

  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const items = await getPageItems(page);
    if (pageNumber === 1) {
      meta = extractHeaderMeta(items);
      quoteSummary = extractQuoteSummary(items);
      columnMaxX = extractColumnBoundaries(items);
    }
    const knownLanguages = new Set(Object.keys(quoteSummary));
    const extracted = extractRows(items, columnMaxX, knownLanguages, currentLanguage);
    currentLanguage = extracted.currentLanguage;
    extracted.rows.forEach((row) => {
      if (!row.text.includes('sub-total')) return;
      const match = row.text.match(DESC_RE);
      const rawLanguage = match ? match[2] : row.headerLanguage;
      if (!rawLanguage) return;
      const category = match ? match[1] : undefined;
      const language = normalizeLanguage(rawLanguage);
      const units = parseFloat(row.units.replace(/,/g, ''));
      const cost = parseFloat(row.cost.replace(' EUR', '').replace(/,/g, ''));
      if (Number.isNaN(units) || Number.isNaN(cost)) return;
      if (!sums[language]) {
        sums[language] = { newWordsUnits: 0, newWordsCost: 0, fuzziesUnits: 0, fuzziesCost: 0 };
      }
      const prefix = /^Fuzzies$/i.test(category || '') ? 'fuzzies' : 'newWords';
      sums[language][`${prefix}Units`] += units;
      sums[language][`${prefix}Cost`] += cost;
    });
  }

  const summary = summarize(sums, quoteSummary);
  if (!Object.keys(summary).length) {
    throw new Error('No quote detail rows were found in this PDF.');
  }
  const { records, warnings } = buildRecords(summary, meta || {});
  return { records, summary, meta, warnings };
}